import {
    Component,
    defineElement,
    FC,
    onDidLoad,
    useBindMethod,
    useElement,
    useListen,
    useProp,
} from '@atomify/hooks';
import { inviewObserver } from '@utilities/inview';
import { Breakpoints, breakpoints, getCurrentBreakpoint, isMobile } from '@utilities/media-query';
import EmblaCarousel, { EmblaCarouselType } from 'embla-carousel';

import { autoPlay } from './autoPlay';

export interface SliderProgressElement extends Component {
    inView: boolean;
    reload: () => void;
}

const INIT_SLIDE_HOOK = 'js-hook-slider-slide-to-here';
const SLIDE_SELECTED_CLASS = 'slider-progress__cell--is-selected';
const SLIDER_START_CLASS = 'slider-progress--start-position';
const SLIDER_END_CLASS = 'slider-progress--end-position';
const SLIDER_HAS_ITEMS_CLASS = 'slider-progress__footer--has-items';

const SliderProgress: FC<SliderProgressElement> = ({ element }) => {
    const [autoSlide] = useProp<boolean>('autoSlide', false);
    const [autoSlideTimeout] = useProp<number>('autoSlideTimeout', 3000);
    const [sliderIfNotGreaterThan] = useProp<Breakpoints | null>('sliderIfNotGreaterThan', null);
    const [slidesToScroll] = useProp<number>('slidesToScroll', 1);
    const [, setInView, watchInView] = useProp<boolean>('inView', false);

    const slider = useElement<HTMLElement>('[js-hook-slider-progress]');
    const sliderFooter = useElement<HTMLDivElement>('[js-hook-slider-progress-footer]');
    const sliderProgress = useElement<HTMLElement>('[js-hook-slider-progress-progress]');
    const sliderPrevious = useElement<HTMLElement>('[js-hook-slider-progress-previous]');
    const sliderNext = useElement<HTMLElement>('[js-hook-slider-progress-next]');

    let embla: EmblaCarouselType | undefined;
    let slideNodes: HTMLElement[];
    let autoPlayer: ReturnType<typeof autoPlay> | undefined;
    let initSlideIndex: number | null = null;
    let currentSlideIndex = 0;
    let isDragging = false;

    onDidLoad(componentDidLoad);
    useListen(sliderNext, 'click', nextSlide);
    useListen(sliderPrevious, 'click', previousSlide);
    useBindMethod('reload', reloadSlider);

    function nextSlide() {
        if (embla?.canScrollNext() || currentSlideIndex + 1 < slideNodes.length) {
            select(currentSlideIndex + 1);
        }
    }

    function previousSlide() {
        if (embla?.canScrollPrev()) select(currentSlideIndex - 1);
    }

    function fillSliderProgress() {
        if (!embla || !sliderProgress.current) return;
        const scrollProgress = ((currentSlideIndex + 1) / slideNodes.length) * 100;
        sliderProgress.current.style.transform = `translate(${scrollProgress}%, -50%)`;
    }

    function setSelectedState() {
        if (!embla) return;

        element.classList.toggle(SLIDER_START_CLASS, currentSlideIndex <= 0);
        element.classList.toggle(SLIDER_END_CLASS, currentSlideIndex >= slideNodes.length - 1);

        slideNodes.forEach((slide, index) =>
            slide.classList.toggle(SLIDE_SELECTED_CLASS, index === currentSlideIndex),
        );
    }

    function select(index: number, options?: { noScroll?: boolean; force?: boolean }) {
        if (currentSlideIndex === index && !options?.force) return;
        if (index < 0) currentSlideIndex = 0;
        else if (index >= slideNodes.length) currentSlideIndex = slideNodes.length - 1;
        else currentSlideIndex = index;
        if (!options?.noScroll) embla?.scrollTo(currentSlideIndex, false);
        setSelectedState();
        fillSliderProgress();
    }

    function addAutoPlay() {
        if (!embla) return;
        autoPlayer = autoPlay(embla, autoSlideTimeout);
        autoPlayer.play();
    }

    watchInView((inView) => {
        if (inView && initSlideIndex) {
            select(initSlideIndex);
            initSlideIndex = null;
        }
    });

    function checkKey(e: KeyboardEvent) {
        if (element.inView) {
            if (e.key == 'ArrowLeft') {
                previousSlide();
            } else if (e.key == 'ArrowRight') {
                nextSlide();
            }
        }
    }

    function initSlider() {
        if (!slider.current) return;

        embla = EmblaCarousel(slider.current, {
            align: 'center',
            slidesToScroll: isMobile() ? 1 : slidesToScroll,
            containScroll: 'trimSnaps',
            dragFree: false,
            skipSnaps: true,
            watchFocus(_, evt) {
                const slide = evt.currentTarget as HTMLElement;
                const index = slide && slideNodes.indexOf(slide);
                if (index >= 0) select(index, { noScroll: true });
                return true;
            },
            watchDrag() {
                isDragging = true;
                return true;
            },
        });

        if (!embla) return;
        slideNodes = embla.slideNodes();

        // Initialize progress when there are more than 1 item.
        if (slideNodes.length > 1) {
            sliderFooter.current?.classList.add(SLIDER_HAS_ITEMS_CLASS);
        }

        // slider will automatically adjust to here when in view
        initSlideIndex = slideNodes.findIndex((slide) => slide.hasAttribute(INIT_SLIDE_HOOK));
        // otherwise adjust to zero immediately
        if (initSlideIndex < 0) {
            select(0, { force: true });
            initSlideIndex = null;
        }

        if (autoSlide) {
            addAutoPlay();
        }

        embla.on('scroll', () => {
            if (!isDragging) return;
            select(embla!.selectedScrollSnap(), { noScroll: true });
        });

        if (autoPlayer) {
            embla.on('pointerDown', () => {
                autoPlayer?.stop();
            });

            embla.on('pointerUp', () => {
                autoPlayer?.play();
            });
        }
    }

    function destroySlider() {
        if (embla) {
            embla.destroy();
            embla = undefined;
        }
    }

    function showAsSlider() {
        if (sliderIfNotGreaterThan) {
            const currentBreakpoint = getCurrentBreakpoint();

            const currentBreakpointIndex = Object.keys(breakpoints).indexOf(
                currentBreakpoint ? currentBreakpoint : '',
            );

            const maximumBreakpointIndex = Object.keys(breakpoints).indexOf(
                sliderIfNotGreaterThan ? sliderIfNotGreaterThan : '',
            );

            return currentBreakpointIndex < maximumBreakpointIndex;
        }

        return true;
    }

    function setupSlider() {
        if (showAsSlider() && !embla) {
            initSlider();
        } else {
            destroySlider();
        }
    }

    function componentDidLoad() {
        if (!slider.current) return;
        setupSlider();
        setSelectedState();

        window.addEventListener('keydown', checkKey);
        inviewObserver.observe(slider.current, setInView);
    }

    function reloadSlider() {
        destroySlider();
        setupSlider();
        setSelectedState();
    }
};

SliderProgress.props = {
    autoSlide: {
        type: Boolean,
    },
    autoSlideTimeout: {
        type: Number,
    },
    sliderIfNotGreaterThan: {
        type: String,
    },
    slidesToScroll: {
        type: Number,
        reflectToAttr: true,
    },
    inView: {
        type: Boolean,
    },
};

defineElement('bpd-slider-progress', SliderProgress);
